import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import RichTextBlock from "./RichTextBlock";
import Gallery from "../components/ImageGallery";

const ListWithIcons = ({ services }) => {
  const [selectedService, setSelectedService] = useState(null);
  React.useEffect(() => {
    if (selectedService) {
      // no-op
    }
  }, [selectedService]);
  // Remapeo de elementos para acceder a los campos directamente
  const elementos = services.elementos.map(e => {
    if (e.fields) {
      return { ...e.fields, ...e.sys, colorTexto: e.fields.colorTexto };
    }
    return e;
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Color de texto global
  const globalTextColor = services.colorTexto && services.colorTexto.trim() ? services.colorTexto : "#222";
  // Color de texto del elemento seleccionado (Contentful: fields.colorTexto)
  const selectedColor = selectedService?.colorTexto && selectedService.colorTexto.trim()
    ? selectedService.colorTexto
    : globalTextColor;

  const handleServiceClick = (service) => {
    setSelectedService((prev) => (prev === service ? null : service));
  };

  if (!services || !services.elementos || services.elementos.length === 0) {
    return <Typography>No hay servicios para mostrar.</Typography>;
  }

  return (
    <Box
      sx={{
        width: "100vw",
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        textAlign: "center",
        background: theme.palette.background.component,
        padding: { xs: "16px 0", sm: "24px 0" },
        boxShadow: "0px 0px 1px rgba(0, 0, 0, 0.5)",
      }}
    >
      <Typography variant="h4" sx={{ marginBottom: "16px" }}>
        {services.titulo}
      </Typography>
      <Box
        className="interactive-elements"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "32px",
        }}
      >
        {elementos.map((service, index) => {
          const isInteractive = !!service.contenido; // sólo interactivo si tiene contenido para mostrar
          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                justifyContent: "center",
                maxWidth: "120px",
                textAlign: "center",
                ...(isInteractive
                  ? {
                      cursor: "pointer",
                      transition: "transform 0.2s",
                      "&:hover": { transform: "scale(1.1)" },
                    }
                  : { cursor: "default" }),
              }}
              onClick={() => isInteractive && handleServiceClick(service)}
            >
              <IconButton
                sx={{
                  width: "70px",
                  height: "70px",
                  marginBottom: "8px",
                }}
              >
                <img
                  src={service.icono}
                  alt={service.titulo}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </IconButton>
              <Typography
                variant="subtitle1"
                color="textPrimary"
                sx={{
                  wordWrap: "break-word",
                  lineHeight: "1.5",
                  maxWidth: "100%",
                }}
              >
                {service.titulo}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Collapse in={!!selectedService}>
        <Paper
            className="paperBox"
            sx={{
              margin: isMobile ? "16px 0 0 0" : "24px 0 0 0",
              maxWidth: "100%",
              padding: isMobile ? "16px" : "32px",
              backgroundImage: selectedService?.background
                ? `url(${selectedService.background})`
                : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              color: selectedColor,
              borderRadius: "0",
              boxShadow: "inset 0px 3px 12px rgba(0, 0, 0, 0.4)",
              mx: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
        >
          {selectedService && (
            <Box
              className="interactive-element"
              sx={{
                // SOLO una columna SIEMPRE
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: isMobile ? "16px" : "24px",
                justifyContent: "center",
                background: "palette.background.component",
              }}
            >
              {/* Galería */}
              {selectedService.componente?.tipo === "galeriaDeImagenes" && (
                <Box
                  sx={{
                    width: { xs: "100vw", sm: "90vw" },
                    maxWidth: "1200px",
                    overflow: "visible",
                  }}
                >
                  <Gallery
                    images={selectedService.componente.imagenes}
                    sx={{
                      borderRadius: "20px",
                      width: "100%",
                    }}
                  />
                </Box>
              )}
              {/* Imagen con link */}
              {selectedService.componente?.tipo === "imageWithLink" && (
                <Box sx={{ textAlign: "left" }}>
                  {selectedService.componente?.image?.fields?.file?.url && (
                    selectedService.componente.link ? (
                      <a
                        href={selectedService.componente.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none" }}
                      >
                        <img
                          src={`https:${selectedService.componente.image.fields.file.url}`}
                          alt={selectedService.titulo || "Imagen"}
                          style={{
                            maxWidth: "100%",
                            height: "auto",
                            borderRadius: "8px",
                            transition: "transform 0.2s",
                            cursor: "pointer",
                          }}
                          onMouseOver={e => e.currentTarget.style.transform = "scale(1.05)"}
                          onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}
                        />
                      </a>
                    ) : (
                      <img
                        src={`https:${selectedService.componente.image.fields.file.url}`}
                        alt={selectedService.titulo || "Imagen"}
                        style={{
                          maxWidth: "100%",
                          height: "auto",
                          borderRadius: "8px",
                          transition: "transform 0.2s",
                        }}
                        onMouseOver={e => e.currentTarget.style.transform = "scale(1.05)"}
                        onMouseOut={e => e.currentTarget.style.transform = "scale(1)"}
                      />
                    )
                  )}
                </Box>
              )}
              {/* RichText y botón */}
              <Box
                sx={{
                  textAlign: "left",
                }}
              >
                <Box
                  className="contenido"
                  sx={{
                    maxWidth: isMobile ? "800px" : "1024px",
                    textAlign: "left",
                    fontSize: isMobile ? "16px" : "24px",
                  }}
                >
                  {selectedService.contenido &&
                    <RichTextBlock content={selectedService.contenido} color={selectedColor} />}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
};

ListWithIcons.propTypes = {
  services: PropTypes.shape({
    titulo: PropTypes.string,
    elementos: PropTypes.arrayOf(
      PropTypes.shape({
        icono: PropTypes.string,
        titulo: PropTypes.string,
        colorTexto: PropTypes.string,
        background: PropTypes.any,
        contenido: PropTypes.any,
        componente: PropTypes.any,
      })
    ),
    colorTexto: PropTypes.string,
  }).isRequired,
};

export default ListWithIcons;